import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ErrorLevel = "feature" | "page" | "root";

interface ErrorFallbackProps {
  /** The error that was caught */
  error: Error;
  /** React error info with component stack */
  errorInfo: React.ErrorInfo | null;
  /** Boundary level determines UI size and layout */
  level: ErrorLevel;
  /** Callback to retry/reset the error boundary */
  onReset: () => void;
  /** Component name for user context */
  componentName: string;
}

export function ErrorFallback({
  error,
  errorInfo,
  level,
  onReset,
  componentName,
}: ErrorFallbackProps) {
  const resetButtonRef = useRef<HTMLButtonElement>(null);
  const [showDetails, setShowDetails] = useState(false);
  const isDevelopment = !import.meta.env.PROD;

  useEffect(() => {
    resetButtonRef.current?.focus();
  }, []);

  const containerClasses = cn("flex flex-col items-center justify-center text-center", {
    "min-h-[400px] p-6 md:p-8": level === "feature",
    "min-h-screen p-8 md:p-12": level === "page",
    "fixed inset-0 z-50 bg-background/95 backdrop-blur-sm p-8 md:p-12": level === "root",
  });

  return (
    <div className={containerClasses} role="alert" aria-live="assertive">
      <div className="space-y-4 max-w-md w-full">
        <div className="flex justify-center">
          <AlertTriangle
            className={cn("text-destructive", level === "root" ? "w-16 h-16" : "w-12 h-12")}
            aria-hidden="true"
          />
        </div>

        <h2 className={cn("font-semibold text-foreground", level === "root" ? "text-2xl md:text-3xl" : "text-xl md:text-2xl")}>
          Something went wrong
        </h2>

        <p className="text-sm md:text-base text-muted-foreground">
          An error occurred while rendering {componentName}. Please try again.
        </p>

        {isDevelopment && (
          <div className="mt-6 text-left w-full">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
              aria-expanded={showDetails}
              aria-controls="error-details"
            >
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <span className="font-medium">Error Details (Development Only)</span>
            </button>

            {showDetails && (
              <div id="error-details" className="space-y-4 text-xs">
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                  <div className="font-semibold text-destructive mb-1">Error Message:</div>
                  <pre className="whitespace-pre-wrap break-words text-foreground/80">{error.message}</pre>
                </div>

                {error.stack && (
                  <div className="bg-muted border border-border rounded-lg p-3 overflow-auto max-h-48">
                    <div className="font-semibold text-foreground mb-1">Stack Trace:</div>
                    <pre className="whitespace-pre-wrap break-words text-muted-foreground">{error.stack}</pre>
                  </div>
                )}

                {errorInfo?.componentStack && (
                  <div className="bg-muted border border-border rounded-lg p-3 overflow-auto max-h-48">
                    <div className="font-semibold text-foreground mb-1">Component Stack:</div>
                    <pre className="whitespace-pre-wrap break-words text-muted-foreground">{errorInfo.componentStack}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <Button
          ref={resetButtonRef}
          onClick={onReset}
          className={cn("mt-6", level === "root" ? "px-8 py-6 text-lg" : "px-6 py-3")}
          size={level === "root" ? "lg" : "default"}
        >
          Try Again
        </Button>
      </div>
    </div>
  );
}
